// Keeps one circle's live state: a realtime subscription plus member-list
// reloads over REST when the stream says the list may be out of date.
import { useEffect, useState } from "react";
import { api, ApiError, session, unwrap } from "../api";
import { CircleStream, streamUrl, type StreamStatus } from "./circleStream";
import { applyFrame, emptyCircle, withMembers, type LiveCircle, type MembershipChange } from "./liveCircle";

const RELOAD_RETRY_MS = 5_000;

export interface LiveCircleView {
  circle: LiveCircle;
  status: StreamStatus;
  /** Whether the member list has loaded at least once. */
  membersLoaded: boolean;
}

/**
 * Live state of `circleId` for the signed-in device session. `onLost` runs when
 * the caller can no longer see the circle (left, removed, deleted). Mount it
 * with `key={circleId}` so a new circle starts from an empty state.
 */
export function useLiveCircle(circleId: string, selfId: string, deviceId: string, onLost: () => void): LiveCircleView {
  const [view, setView] = useState<LiveCircleView>(() => ({
    circle: emptyCircle(circleId),
    status: "connecting",
    membersLoaded: false,
  }));
  const [lost, setLost] = useState(false);

  useEffect(() => {
    let disposed = false;
    let current = emptyCircle(circleId);
    // Orders stream frames against member-list responses (see LiveCircle.pausedAsOf).
    let clock = 0;
    let reloading = false;
    /** Membership frames applied while a member-list request is in flight. */
    let sinceRequest: MembershipChange[] = [];
    let reloadAgain = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const publish = (circle: LiveCircle, patch: Partial<Omit<LiveCircleView, "circle">> = {}) => {
      current = circle;
      if (!disposed) setView((prev) => ({ ...prev, ...patch, circle }));
    };

    const markLost = () => {
      stream.stop();
      if (!disposed) setLost(true);
    };

    const reload = async () => {
      if (reloading) {
        reloadAgain = true;
        return;
      }
      reloading = true;
      sinceRequest = [];
      clearTimeout(retryTimer);
      try {
        const list = unwrap(await api.GET("/circles/{circleId}/members", { params: { path: { circleId } } }));
        if (disposed) return;
        publish(withMembers(current, list, ++clock, sinceRequest), { membersLoaded: true });
      } catch (err) {
        if (disposed) return;
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
          markLost();
          return;
        }
        retryTimer = setTimeout(() => void reload(), RELOAD_RETRY_MS);
      } finally {
        reloading = false;
        if (reloadAgain && !disposed) {
          reloadAgain = false;
          void reload();
        }
      }
    };

    const stream = new CircleStream({
      url: streamUrl(window.location.origin),
      circleId,
      deviceId,
      connect: (url) => new WebSocket(url),
      session: () => session.activeSession(),
      refresh: (stale) => session.refresh(stale),
      cursor: () => current.cursor,
      onStatus: (status) => {
        if (!disposed) setView((prev) => ({ ...prev, status }));
      },
      onFrame: (frame) => {
        if (disposed) return;
        const { circle, effect } = applyFrame(current, frame, ++clock, selfId);
        if (reloading && frame.type === "membership" && frame.circle_id === circleId) sinceRequest.push(frame);
        // Publish the frame before any reload, so a failing reload can't keep a hidden member on screen.
        publish(circle);
        if (effect === "reload_members") void reload();
        else if (effect === "circle_lost") markLost();
      },
    });
    stream.start();
    void reload();

    const online = () => stream.nudge();
    window.addEventListener("online", online);
    return () => {
      disposed = true;
      window.removeEventListener("online", online);
      clearTimeout(retryTimer);
      stream.stop();
    };
  }, [circleId, selfId, deviceId]);

  useEffect(() => {
    if (lost) onLost();
  }, [lost, onLost]);

  return view;
}
